import { z } from '../zod';
import {
  OrderBumpShowHideComparisonValidationSchema,
  OrderBumpShowHideSubjectValidationSchema,
} from './enum';

export const OrderBumpResponseValidationSchema = z
  .object({
    productId: z.string().meta({ description: 'ID of the order bump product.' }),
    addedAt: z.string().nullable().meta({
      description: 'ISO 8601 timestamp when the order bump was added to this page.',
    }),
    enabled: z.boolean().meta({ description: 'Whether the order bump is offered.' }),
    preselected: z.boolean().meta({ description: 'Whether it is ticked when the page loads.' }),
    hidden: z.boolean().meta({
      description: 'Whether it is hidden unless the page URL reveals it with `?show_order_bumps=`.',
    }),
    showHideLogic: z
      .object({
        enabled: z.boolean(),
        subject: OrderBumpShowHideSubjectValidationSchema,
        comparison: OrderBumpShowHideComparisonValidationSchema,
        priceIds: z.array(z.string()),
        ticketTypeIds: z.array(z.string()),
        orderBumpProductIds: z.array(z.string()),
      })
      .meta({ description: 'Rule that shows the order bump only for some selections.' }),
    allowQuantity: z.boolean().meta({ description: 'Whether the customer can pick a quantity.' }),
    shortenDescription: z.boolean().meta({
      description: 'Whether a long description is clamped behind a Show more toggle.',
    }),
    product: z
      .object({
        id: z.string(),
        title: z.string().meta({
          description: 'Product title, or the product ID when the product no longer exists.',
        }),
        currency: z.string().nullable(),
        imageUrl: z.string().nullable().meta({ description: 'First product image, if any.' }),
      })
      .meta({
        description: 'Summary of the order bump product. Read the full product with get_product.',
      }),
    price: z
      .object({
        amount: z.number().meta({ description: 'Price in the smallest currency unit.' }),
        currency: z.string(),
        billingType: z.enum(['one_time', 'recurring']),
        interval: z.string().nullable(),
        intervalCount: z.number().nullable(),
      })
      .nullable()
      .meta({
        description: 'The price the order bump is offered at. Null when it is unavailable.',
      }),
    unavailableReason: z.string().nullable().meta({
      description:
        'Why the order bump cannot be offered on this page right now (e.g. a currency mismatch or a deleted product), or null when it can. An unavailable order bump stays on the page until removed.',
    }),
    usedOnPageCount: z.number().meta({
      description: 'How many of your pages offer this product as an order bump.',
    }),
    hasPurchases: z.boolean().meta({
      description: 'Whether the product has been bought. A bought product cannot be deleted.',
    }),
  })
  .meta({ id: 'OrderBump' });

export const OrderBumpDetailResponseValidationSchema = z
  .object({ data: OrderBumpResponseValidationSchema })
  .meta({ description: 'An order bump placed on a checkout page or event.' });

export const OrderBumpListResponseValidationSchema = z
  .object({ data: z.array(OrderBumpResponseValidationSchema) })
  .meta({ description: 'The order bumps on a checkout page or event, in display order.' });

export type OrderBumpResponse = z.infer<typeof OrderBumpResponseValidationSchema>;
